// The app shell: a slim header (wordmark/back-to-catalog link, theme toggle,
// sign out) + the routed content area. No sidebar, unlike admin's: `bi_site` at
// `read` reaches every dashboard, so an area-nav rail would list nothing.
//
// ONE nav entry is conditional: Permissions needs `bi_site` at `full`, and a
// link that always 403s teaches readers to distrust the nav. The route itself
// stays open (router.tsx). `canWrite` arriving late means the entry appears a
// beat after the rest.
import { useQuery } from "@tanstack/react-query";
import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { Button, IconButton } from "@bi/shared";

import { PreviewBanner } from "./preview-banner";
import { previewAppliesToPath } from "../lib/preview-catalog";
import { dashboardsQuery, taggingIdentityQuery } from "../lib/queries";
import { SearchPalette } from "./search-palette";
import { signOutBi } from "../lib/sign-out";
import { useTheme } from "../lib/theme-client";
import { ChartIcon, MoonIcon, SunIcon } from "./icons";

function ThemeToggle() {
  const { theme, toggle } = useTheme();
  const dark = theme === "dark";
  return (
    <IconButton
      variant="ghost"
      tone="neutral"
      size="sm"
      aria-label={dark ? "Switch to light theme" : "Switch to dark theme"}
      title={dark ? "Light theme" : "Dark theme"}
      onClick={toggle}
    >
      {dark ? <SunIcon size={18} /> : <MoonIcon size={18} />}
    </IconButton>
  );
}

export function AppShell() {
  // The SAME query `Boot()` already resolved, so this costs no request: it is
  // read here because the backend's answer is the only honest source for "which
  // catalog am I reading" (see preview-banner.tsx). `undefined` while it is in
  // flight and for the environment's own catalog, which is the ordinary case
  // and draws nothing.
  const catalog = useQuery(dashboardsQuery).data?.catalog;
  // And only on the pages the selection actually changes. The router retains
  // `?catalog=` everywhere, deliberately, but the studio, the taggers, the
  // corpus and the documents are this environment's own data whatever it says,
  // so a banner promising "nothing here is merged" over them would be the
  // inverse of the claim it exists to make.
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const me = useQuery(taggingIdentityQuery);
  return (
    <div className="bi-shell">
      <header className="bi-header">
        <Link to="/" className="bi-brand">
          <span className="bi-brand__mark" aria-hidden="true">
            <ChartIcon size={20} />
          </span>
          <span className="bi-brand__text">Mira BI</span>
        </Link>
        <nav className="bi-header__nav">
          <Link to="/" className="bi-header__link">
            Dashboards
          </Link>
          <Link to="/experiments" className="bi-header__link">
            Experiments
          </Link>
          <Link to="/ask" className="bi-header__link">
            Ask
          </Link>
          <Link to="/studio" className="bi-header__link">
            Studio
          </Link>
          <Link to="/taggers" className="bi-header__link">
            Taggers
          </Link>
          <Link to="/corpus" className="bi-header__link">
            Corpus
          </Link>
          {me.data?.canWrite ? (
            <Link to="/permissions" className="bi-header__link">
              Permissions
            </Link>
          ) : null}
        </nav>
        <div className="bi-header__tools">
          <SearchPalette />
          <ThemeToggle />
          <Button variant="ghost" tone="neutral" size="sm" onClick={() => signOutBi()}>
            Sign out
          </Button>
        </div>
      </header>
      {catalog && previewAppliesToPath(pathname) ? <PreviewBanner catalog={catalog} /> : null}
      <main className="bi-content">
        <Outlet />
      </main>
    </div>
  );
}
